/**
 * How a script scrolls: smoothly, unless the Member asks for reduced motion. A script's smooth
 * scroll is not CSS's, so the reduced-motion rule in tokens.css can't flatten it: the machine is
 * asked here.
 */
export function scrollBehavior(): ScrollBehavior {
  return matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth"
}

/** Whether an open overlay, such as a docked sheet, holds the page still, as Base UI's lock does. */
const pageHeld = () =>
  [document.documentElement, document.body].some((element) =>
    /hidden|clip/.test(getComputedStyle(element).overflowY),
  )

/**
 * Scrolls `element` into view (`block`, through `scrollBehavior()`) once no overlay holds the page:
 * a smooth scroll begun under a closing sheet's lock stalls, then snaps when the lock lets go. It
 * waits for the lock to go and scrolls the frame after. Returns how to call it off.
 */
export function scrollIntoViewWhenFree(element: Element, block: ScrollLogicalPosition): () => void {
  let frame = 0
  const scroll = () => {
    frame = requestAnimationFrame(() =>
      element.scrollIntoView({ block, behavior: scrollBehavior() }),
    )
  }
  if (!pageHeld()) {
    element.scrollIntoView({ block, behavior: scrollBehavior() })
    return () => undefined
  }
  const watch = new MutationObserver(() => {
    if (pageHeld()) return
    watch.disconnect()
    scroll()
  })
  for (const held of [document.documentElement, document.body])
    watch.observe(held, { attributes: true })
  return () => {
    watch.disconnect()
    cancelAnimationFrame(frame)
  }
}
